import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogEnvelope, ControlAck } from '../types/catalog-service.api.types';
import type {
  ArchiveResult,
  CurationRunOutcomes,
  CurationRunProductsPage,
  DeliveryRejectionsPage,
  FieldSets,
  ProductRescoreAck,
  ProductRescoreRequest,
  RunToggle,
  StageAttemptHistory,
  StageDecision,
} from '../types/run-detail.api.types';
import { toSearchParams, unwrap } from '../utils/envelope';

import { readRunMembers } from './curation.service';

// Finding 5 — `getCurationRunStats` used to be duplicated here (no 422 `LEDGER_TOO_LARGE`
// handling) and in curation.service.ts (handles it via `readRunMembers`), both cached under the
// same query key; re-export the single implementation so every caller gets the same behaviour.
export { getCurationRunStats } from './curation.service';

/** Every attempt of one stage, oldest first — the retry history `/progress` hides. */
export async function getStageAttempts(
  runRef: string,
  stage: string,
): Promise<StageAttemptHistory> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.stageAttempts(runRef, stage))
    .json<CatalogEnvelope<StageAttemptHistory>>();
  return unwrap(body);
}

/**
 * Finding 13 — one keyset page of a run's rejected delivery items (newest report per
 * product×destination), plus the whole run's reason breakdown. New route (B1); callers go
 * through `readWithFallback`-style 404/405 detection (`deliveryRejections` capability) since
 * there is nothing to fall back to — an older deploy simply has none of this.
 */
export async function getDeliveryRejections(
  runRef: string,
  params: { after?: string; size: number },
): Promise<DeliveryRejectionsPage> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.deliveryRejections(runRef), {
      searchParams: toSearchParams(params),
    })
    .json<CatalogEnvelope<DeliveryRejectionsPage>>();
  return unwrap(body);
}

/** Requested / honoured / delivered / enriched field sets — the "What ships" panel. */
export async function getFieldSets(runRef: string): Promise<FieldSets> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.fieldSets(runRef))
    .json<CatalogEnvelope<FieldSets>>();
  return unwrap(body);
}

/** Decide at the open gate. 409 = no gate open at that stage any more (decided elsewhere). */
export async function postStageDecision(
  runRef: string,
  decision: StageDecision,
): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.operations.stageDecision(runRef), { json: decision })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** Hold the run at its next stage boundary; a stage already computing still finishes. */
export async function pauseRun(runRef: string, toggle: RunToggle): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.operations.pause(runRef), { json: toggle })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** Release a paused run; `applied:false` when it was not paused. */
export async function resumeRun(runRef: string, toggle: RunToggle): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.operations.resume(runRef), { json: toggle })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** What the run's curation did (new, matched, flagged, …) — each count drills into the product master. */
export async function getCurationRunOutcomes(runId: string): Promise<CurationRunOutcomes> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.curation.runOutcomes(runId))
    .json<CatalogEnvelope<CurationRunOutcomes>>();
  return unwrap(body);
}

/**
 * One page of the run's feed products. This route returns its own page shape
 * (`counts` + `pagination` + `items` inside `data`), not the list envelope.
 */
export async function getCurationRunProducts(
  runId: string,
  params: {
    page: number;
    size: number;
    needsAttention?: boolean;
    sortBy?: string;
    /** `summary` = list-row keys only (incl. `additional_image_urls`, image assets) — §50. */
    fields?: 'summary' | 'full';
  },
): Promise<CurationRunProductsPage> {
  // An old run past the ledger cap (422 LEDGER_TOO_LARGE) reads the rows it still holds.
  const { data } = await readRunMembers(runId, async (source) => {
    const body = await catalogServiceClient
      .get(endpoints.catalogService.curation.runProducts(runId), {
        searchParams: toSearchParams({ ...params, source }),
      })
      .json<CatalogEnvelope<CurationRunProductsPage>>();
    return unwrap(body);
  });
  return data;
}

/**
 * Take this run off the default list. No body: archive is keyed on the path id
 * alone. A run still in flight or delivering 409s (`RUN_IN_FLIGHT` /
 * `RUN_DELIVERING`); repeating it on an already-archived run is a no-op.
 */
export async function archiveRun(runId: string): Promise<ArchiveResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.runs.archive(runId))
    .json<CatalogEnvelope<ArchiveResult>>();
  return unwrap(body);
}

/** The toggle's other direction — restore an archived run to the default list. */
export async function unarchiveRun(runId: string): Promise<ArchiveResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.runs.unarchive(runId))
    .json<CatalogEnvelope<ArchiveResult>>();
  return unwrap(body);
}

/**
 * Trigger an on-demand re-score of one enriched product. 202 `PENDING` once
 * content-generation has the request, or 200 `ALREADY_SCORED` when a fresh
 * on-demand score already exists (send `overrideCache` to force it anyway);
 * poll `getProductScore` (content-generation's read-back) for the result.
 */
export async function postProductRescore(
  runRef: string,
  productId: string,
  request: ProductRescoreRequest,
): Promise<ProductRescoreAck> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.operations.productScore(runRef, productId), { json: request })
    .json<CatalogEnvelope<ProductRescoreAck>>();
  return unwrap(body);
}
